
/**
 * `BackportPluginComponent`
 * - Allows externals SPAs to be loaded onto our site (backport of nn.plugincomponent.es6)
 *
 * ### Warning: Weird code content warning!
 *
 * We need to dinamically load JS and CSS into the IFrame *outside* the
 * Vue lifecycle. Apparently this messes everything up if we then try to
 * change the DOM via Vue. For example, this sequence of events:
 * 1. There is an `isInitializing` data property, which begins as `true`
 * 2. There is a blank IFrame in the DOM which has a class if
 * `isInitializing` is `true`
 * 3. We load JS and CSS into the IFrame DOM
 * 4. As soon as everything is loaded, we set `isInitializing` to `false`
 * 5. !! The contents of the IFrame are reset back to none
 *
 * What we think is happening is that as soon as `isInitializing` changes
 * value, Vue will check its template DOM to see what needs changing,
 * and since the IFrame under its domain has diffs, it resets it back
 * to its proper empty state, as defined by the template.
 *
 * What this means in practice is that we can't do any DOM manipulation
 * via Vue's very handy system, and instead need to do it manually. There's
 * probably some very clever way of doing this via VNodes, but we're doing
 * this via normal DOM manipulation because that's too much work. Sorry,
 * future maintainers. Sacrifices must be made.
 *
 *
 * ### Multiple SPA's on a single page
 *
 * We can load multiple SPA's on a single page, although there are a few things to consider.
 * iFrame Hash Manager is working perfectlly with one SPA running. If we run two SPA's in the same page
 * everywthing loads, the parameter changes as we change things on the SPA but if we reload it, the SPA goes
 * back to it's initial state.
 *
 * ### Warning: Unnused code content warning!
 *
 * PluginMediator code was used at times, but as far as our research went,
 * the particular use of PluginMediator by this component is to warn top-level
 * code that an SPA had been loaded. In practice, this was found to only be
 * necessary for FSS content.
 *
 * As the code is not necessary for this PoC it was left commented. In case it
 * becomes necessary, uncomment the areas with the mark `%PluginMediator`
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import Loading from '@owc-packages/Loading/src/Loading.vue';
import { iframeResizer } from 'iframe-resizer';
import iframeHashManager from 'iframe-hash-manager';
// We have to import this as raw to inject in the iframe via DOM manipulation
import iframeResizeContentWindow from '!!raw-loader!iframe-resizer/js/iframeResizer.contentWindow';
import messageBusClient from '!!raw-loader!./emb-messagebus-client';
import messageBusServer from '!!raw-loader!./emb-messagebus-server';
import {
  LifecycleChannelInstance,
  IframeEventTarget,
  MessagebusPayload,
  PluginComponentIFrame,
  BackPortPluginComponent,
} from './BackportPluginComponent';
import {
  VALUES,
  MESSAGE_BUS_MESSAGETYPE,
  CIAM_SESSION_STORAGE_KEYS,
  CIAM_LOCAL_STORAGE_KEYS,
  WKO_NAMES,
  ME_SESSION_STORAGE_KEYS,
  VEHICLE_CONTEXT_SESSION_STORAGE_KEYS,
  WKO_FIELDS,
  PLUGIN_COMPONENT_EVENTS,
  DEALER_CONTEXT_STORAGE_FIELDS,
  MESSAGE_BUS_EVENTS,
} from './BackportPluginComponent.config';
import Lifecycle from './Lifecycle';
import Connection from './Connection';
import Logger from '@owc-utils/common/Logger';
import * as CLASS_NAMES from '@owc-utils/common/ClassNames';
import WKOMixin from '@owc-utils/Mixins/WKO';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { WellKnownObjects } from '@owc/connection-wko-manager';
import initializeTracking from '@owc-packages/Tracking/src/TrackingUtils';
import { EventDetailPayload } from '@owc-packages/Tracking/src/Tracking';

// %PluginMediator
// Create plugin mediator and make it available in the global scope
// const pluginMediator = new PluginMediator();
// (window as any).pluginMediator = pluginMediator;

const MODULE_NAME = 'owc-backport-plugin-component';

const rawScripts = [messageBusClient, messageBusServer, iframeResizeContentWindow];

// FIXME: remove after CINOWF-30449 is implemented
let isHackyTimeoutExpired = false;
let isInitEventFired = false;

export default (ComponentInitializeDispatcher as BackPortPluginComponent).extend({
  name: MODULE_NAME,
  components: {
    Loading,
  },
  mixins: [OwcDataComponent, WKOMixin],
  data: function() {
    return {
      spaConfig: {
        instanceId: '',
        pluginJSUrl: '',
        appsJSUrl: [],
      },
      iframeContentDocument: null,
      lifecycleChannel: null,
      pluginInstanceId: '',
      wkoParameterNames: [],
    };
  },
  computed: {
    configUrl: function() {
      return this.componentData?.configUrl || null;
    },
    config: function() {
      return {
        ...VALUES,
        ...this.spaConfig,
      };
    },
    iframeSrc: function() {
      // `this.componentData.blankSrc` is the PROD url
      // `VALUES.iFrameContextUrl` is the one for dev/localhost environment
      return this.componentData?.blankSrc || VALUES.iFrameContextUrl;
    },
    scriptContainerClass: function() {
      return `${this.config.instanceId}${VALUES.clientJSClass}`;
    },
    classNames: function() {
      return CLASS_NAMES;
    },
  },
  beforeDestroy() {
    this.removeEventListeners();
    const iframeElement = this.$el.firstChild;

    if (this.lifecycleChannel) {
      // Triggering the destroy event
      this.triggerMessagebusEvent(
        this.lifecycleChannel,
        this.config.data,
        MESSAGE_BUS_MESSAGETYPE.destroy,
        () => {
          // Removing listeners from iframe resizer
          (iframeElement as PluginComponentIFrame).iFrameResizer.close();
        },
      );
    }
  },
  async mounted() {
    this.trackingInstance = await initializeTracking();
    iframeHashManager();
    // FIXME: remove after CINOWF-30449 is implemented
    // allowing WKO manager 5 secs before trying with
    // possibly wrong data from session storage
    // so plugins will not be stuck in loading state
    // allowing plugins to recover, if able
    setTimeout(() => {
      isHackyTimeoutExpired = true;
      if (!isInitEventFired) {
        this.initializeMessageBus();
        Logger.info('[BackportPluginComponent] init via session storage fallback');
      }
    }, 5000);
  },
  methods: {
    loadConfigUrl: async function() {
      if (this.configUrl !== null) {
        const response = await window.fetch(this.configUrl);
        this.spaConfig = await response.json();
      }
    },
    onIframeFirstLoad: async function(loadEvent: Event) {
      if (loadEvent.target) {
        this.iframeContentDocument = (loadEvent.target as IframeEventTarget).contentDocument;
      }
      await this.loadConfigUrl();
      this.setup();
    },
    setup: function() {
      if (this.iframeContentDocument == null) {
        Logger.error('[BackportPluginComponent] iframe has no contentDocument');
        return;
      }

      const elements = this.scaffoldIframe(this.iframeContentDocument);
      if (elements == null) {
        return;
      }

      const [cssContainer, jsContainer] = elements;
      this.loadDependencies(cssContainer, jsContainer).then(() => {
        this.addEventListeners();
        this.resizeIframe();
        const WKOChangeListener = (state: WellKnownObjects) => {
          this.WKOs = state;
          if (this.isDealerContextNeededAndFixHacky()) {
            Logger.info('[BackportPluginComponent] init via WKO manager');
            isInitEventFired = true;
            this.initializeMessageBus();
            this.unSubscribe(WKOChangeListener);
          }
        };
        this.onWKOChange(WKOChangeListener);
      });
    },
    // FIXME: remove after CINOWF-30449 is implemented
    isDealerContextNeededAndFixHacky() {
      return Boolean(
        !isInitEventFired && //if init has already been fired, don't re-trigger
          this.config.data.params.DealerContext === 'DealerContext' &&
          this.WKOs?.DealerContext,
      );
    },
    scaffoldIframe: function(iframeContentDocument: HTMLIFrameElement) {
      const template = this.$refs.containerTemplate as HTMLTemplateElement;
      const container = template.cloneNode(true) as Element;

      const spaElement = document.createElement(this.config.instanceId);
      spaElement.setAttribute('app-instance-id', this.config.instanceId);
      container.appendChild(spaElement);

      const body = iframeContentDocument.querySelector('body');
      const head = iframeContentDocument.querySelector('head');
      const scriptContainer = container.querySelector(`.${this.scriptContainerClass}`);
      if (body == null || head == null || scriptContainer == null) {
        Logger.error('[BackportPluginComponent] iframe has no head, body, or script container');
        return null;
      }
      if (body != null) {
        body.appendChild(container);
      }

      return [head, scriptContainer];
    },
    loadDependencies: async function(cssContainer: Element, jsContainer: Element) {
      this.loadCssDependencies(this.config.appsCSSUrl || [], cssContainer);
      await this.loadJsDependencies(this.config.appsJSUrl, jsContainer);
    },
    loadCssDependencies(dependencies: string[], container: Element) {
      const styleSheetUrl = this.config.styleSheetUrl || '';
      const allDependencies: string[] = [styleSheetUrl];
      const hasWorkbenchDependency = dependencies.findIndex(url => url.includes('workbench'));

      // When SPAs do not provide their own workbench files
      // we load ours onto them
      if (!hasWorkbenchDependency) {
        // Fetch all stylesheet links
        const styleElements = Array.from(document.head.querySelectorAll('link[rel=stylesheet]'));

        // concat the workbench ones into the dependencies array
        allDependencies.concat(
          styleElements
            .map(element => element.getAttribute('href') || '')
            .filter(href => href.includes('/workbench/')),
        );
      }

      // Add dependencies onto the iframe css container element
      for (const cssDependency of allDependencies) {
        const link = document.createElement('link');
        link.setAttribute('type', 'text/css');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', cssDependency);
        container.appendChild(link);
      }
    },
    async loadJsDependencies(dependencies: string[] = [], container: Element) {
      rawScripts.forEach(script => {
        const scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'text/javascript');
        scriptElement.textContent = script;
        container.appendChild(scriptElement);
      });

      // Filter dependencies and check if it includes "apps-iframe" in it
      const jsDependencies: string[] = dependencies.filter(dependency => !dependency.includes('apps-iframe'));

      const allDependencies = [...jsDependencies, this.config.pluginJSUrl];
      // Ensure all dependencis are loaded sequentially to avoid race effects
      const scriptLoader = this.getSequentialScriptLoader(allDependencies, container);
      for await (const scriptName of scriptLoader) {
        Logger.info('[BackportPluginComponent] js loaded', scriptName);
      }
    },
    triggerMessagebusEvent: function(
      lifecycleChannel: LifecycleChannelInstance,
      payload: MessagebusPayload,
      messageType: string,
      callback: Function,
    ) {
      // create copy of payload, since we want to modify the object locally only
      const messagebusPayload = Object.assign({}, payload);
      const isLifecycleEvent =
        messageType === MESSAGE_BUS_MESSAGETYPE.init ||
        messageType === MESSAGE_BUS_MESSAGETYPE.update ||
        messageType === MESSAGE_BUS_MESSAGETYPE.destroy;

      if (isLifecycleEvent) {
        if (messageType === MESSAGE_BUS_MESSAGETYPE.init) {
          const availableWKOValues = Object.values(WKO_NAMES);
          for (const paramName in messagebusPayload.params) {
            const valueToCompare = messagebusPayload.params[paramName];
            if (paramName !== WKO_NAMES.locale && availableWKOValues.includes(valueToCompare)) {
              this.wkoParameterNames.push(paramName);
            }
          }
        }
        this.replaceParameterValuesInPayload(messagebusPayload);

        Logger.debug(
          '[BackportPluginComponent]',
          `parameter for (${this.config.instanceId}) on messageType ${messageType}: `,
          messagebusPayload,
        );

        lifecycleChannel.publishLifecycleMessage(
          messageType, // lifecycle messageType one of ["init", "update", "destroy"]
          messagebusPayload,
          (withResponse: object) => {
            if (withResponse) {
              if (messageType !== MESSAGE_BUS_MESSAGETYPE.destroy) {
                // SPA ready is read, update the DOM
                // Refer to file documentation as to why this has to be done like this
                (this.$refs.containerTemplate as Element).remove();
                (this.$refs.iframe as Element).classList.remove(
                  this.classNames.isInvisible,
                  this.classNames.hasNoHeight,
                );
                // Since the loader is a Vue Component it needs to be removed like this
                // And for some reason calling `Vue.$destroy` does not remove the element
                // from the DOM, so the additional `loader.$el.remove` is needed.
                const loader = this.$refs.loader as Vue;
                loader.$el.remove();
                loader.$destroy();
              }

              if (callback) {
                callback();
              }
            } else {
              throw new Error(
                `[BackportPluginComponent] Did not receive the expected response event within ${VALUES.responseTimeOutMs} + ms!`,
              );
            }
          },
        );
      } else {
        // other non SPA lifecycle events ("overlayIsOpened", etc)
        lifecycleChannel.publish(messageType, messagebusPayload);
      }
    },
    initializeMessageBus: function() {
      // Initialize Connection and Lifecycle
      const connection = Connection.getConnection(
        `AEM-${this.config.instanceId}`,
        this.$refs.iframe as HTMLIFrameElement,
      );
      this.lifecycleChannel = new Lifecycle(connection, this.config.instanceId);
      this.pluginInstanceId = this.lifecycleChannel.getPluginInstanceId();

      // Forward SPAs analytics events triggered to OneWeb Data Layer handler
      this.lifecycleChannel.subscribe(
        MESSAGE_BUS_EVENTS.analyticsEventTriggered,
        (payload: EventDetailPayload) =>
          this.trackingInstance.customTrackingObjectUpdate(payload, 'custom', undefined),
      );

      // Trigger SPA start up
      this.triggerMessagebusEvent(
        this.lifecycleChannel,
        this.config.data,
        MESSAGE_BUS_MESSAGETYPE.init,
        () => {
          // INTENTIONALLY LEFT BLANK
          // %PLUGINMEDIATOR
          // IF ANYONE NEEDS THE ACTUAL EXPECTED CONTENT OF THIS, UNCOMMENT THE LINES BELOW
          // PLUGINMEDIATOR.PUB('PLUGINCOMPONENT:INITIALIZED', {
          //   LIFECYCLECHANNEL: THIS.LIFECYCLECHANNEL,
          // }),
        },
      );
    },
    resizeIframe: function() {
      iframeResizer({ heightCalculationMethod: 'taggedElement' }, this.$refs.iframe as HTMLElement);
    },
    getSequentialScriptLoader: async function*(dependencies: string[], target: Element) {
      for (const jsDependency of dependencies) {
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('async', 'false');
        script.setAttribute('src', jsDependency);
        yield new Promise(resolve => {
          script.addEventListener('load', () => resolve(`success: ${jsDependency}`));
          script.addEventListener('error', () => resolve(`failed: ${jsDependency}`));
          target.appendChild(script);
        });
      }
    },
    generateWkoFields: function(
      wkoFields: Record<string, string>,
      STORAGE_KEYS_OBJECT: Record<string, string>,
    ) {
      return Object.keys(wkoFields).reduce((acc: Record<string, string | null>, item) => {
        const storageField = wkoFields[item];
        acc[item] = window.sessionStorage.getItem(STORAGE_KEYS_OBJECT[storageField]);
        return acc;
      }, {});
    },
    getPayloadParamValue: function(payloadParamName: string) {
      const token = window.sessionStorage.getItem(CIAM_SESSION_STORAGE_KEYS.jws);
      const hasEverLoggedIn = window.localStorage.getItem(CIAM_LOCAL_STORAGE_KEYS.everLoggedIn);
      switch (payloadParamName) {
        case WKO_NAMES.user: {
          const completeUserProfileData = window.sessionStorage.getItem(ME_SESSION_STORAGE_KEYS.wkoUserData);
          if (completeUserProfileData) {
            return JSON.parse(completeUserProfileData);
          }

          const userFields = this.generateWkoFields(WKO_FIELDS.user, CIAM_SESSION_STORAGE_KEYS);

          return userFields;
        }
        case WKO_NAMES.authentication: {
          let rolesArray: string[] | string =
            window.sessionStorage.getItem(CIAM_SESSION_STORAGE_KEYS.roles) || [];

          if (!Array.isArray(rolesArray)) {
            rolesArray = rolesArray.split(',');
          }

          const authFields = this.generateWkoFields(WKO_FIELDS.authentication, CIAM_SESSION_STORAGE_KEYS);

          return {
            ...authFields,
            token: token,
            roles: rolesArray,
          };
        }
        case WKO_NAMES.anonymousUser: {
          return {
            wasLoggedInBefore: hasEverLoggedIn,
          };
        }
        case WKO_NAMES.selectedAssociatedVehicle: {
          return JSON.parse(
            window.sessionStorage.getItem(VEHICLE_CONTEXT_SESSION_STORAGE_KEYS.selectedAssociatedVehicle) ||
              '{}',
          );
        }
        case WKO_NAMES.userAssociatedVehicles: {
          const storageValue =
            window.sessionStorage.getItem(VEHICLE_CONTEXT_SESSION_STORAGE_KEYS.userAssociatedVehicles) ||
            '[]';
          return JSON.parse(storageValue);
        }
        case WKO_NAMES.selectedAssociatedVehicleDealers: {
          let dealerWko = null;
          const selectedAssociatedVehicle = JSON.parse(
            window.sessionStorage.getItem(VEHICLE_CONTEXT_SESSION_STORAGE_KEYS.selectedAssociatedVehicle) ||
              '{}',
          );
          const selectedAssociatedVehicleDealers = JSON.parse(
            window.sessionStorage.getItem(ME_SESSION_STORAGE_KEYS.userAssociatedDealers) || '{}',
          );

          if (selectedAssociatedVehicle && selectedAssociatedVehicleDealers) {
            const currentSelectedAssociatedDealer =
              selectedAssociatedVehicleDealers[selectedAssociatedVehicle.finOrVin];
            if (currentSelectedAssociatedDealer) {
              dealerWko = currentSelectedAssociatedDealer.reduce(
                (acc: { [key: string]: Record<string, string> }, currentDealer: Record<string, string>) => {
                  if (currentDealer) {
                    if (currentDealer.dealerType === 'SALES') {
                      acc.salesDealer = currentDealer;
                    } else if (currentDealer.dealerType === 'SERVICE') {
                      acc.serviceDealer = currentDealer;
                    }
                  }
                  return acc;
                },
                {},
              );
            }
          }
          return dealerWko;
        }

        case WKO_NAMES.DealerContext: {
          if (isHackyTimeoutExpired) {
            // FIXME: remove after CINOWF-30449 is implemented
            const dealerContextField = window.sessionStorage.getItem(
              DEALER_CONTEXT_STORAGE_FIELDS.dealerContext,
            );
            if (dealerContextField) {
              return JSON.parse(dealerContextField);
            }
            return '';
          } else return this.WKOs?.DealerContext || '';
        }
      }
    },
    replaceParameterValuesInPayload: function(payload: MessagebusPayload) {
      // iterate over dynamic parameters in the payload, whose values need to be replaced
      for (const paramName of this.wkoParameterNames) {
        // Composing the WKO object
        payload.params[paramName] = this.getPayloadParamValue(payload.params[paramName]);
      }
    },
    updateEvent(event: CustomEventInit) {
      if (event.detail && event.detail.params) {
        if (this.lifecycleChannel) {
          this.triggerMessagebusEvent(
            this.lifecycleChannel,
            event.detail,
            MESSAGE_BUS_MESSAGETYPE.update,
            () => {
              // This is the PLUGINMEDIATOR part. Still not sure what to do with it, so I left a comment :D
            },
          );
        }
      }
    },
    addEventListeners() {
      /*
        The listeners are being added to the document since most of the code is still on the old codebase
        and the events are being dispatched to the document.
      */
      document.addEventListener(PLUGIN_COMPONENT_EVENTS.plugincomponentSendUpdateMessage, this.updateEvent);
    },
    removeEventListeners() {
      document.removeEventListener(
        PLUGIN_COMPONENT_EVENTS.plugincomponentSendUpdateMessage,
        this.updateEvent,
      );
    },
  },
});
